import { RefObject, useEffect } from "react";

const useOutsideClick = (
  ref: RefObject<HTMLElement | null>,
  setClickedInside: (inside: boolean) => void,
) => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        // clicked outside ref
        setClickedInside(false);
      }
    };
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);
};

// Dismiss on outside pointer-down or Escape; bound only while open, capture-phase so a stopPropagation can't swallow it. Shared by the three card menus so they dismiss alike.
export const useDismissOnOutsideOrEscape = (
  open: boolean,
  ref: RefObject<HTMLElement | null>,
  close: () => void,
) => {
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("mousedown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ref]);
};

export default useOutsideClick;
